import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LEVEL_XP_THRESHOLDS, MAX_LEVEL } from '../config/levels';

const AnimatedLevelProgress = ({ currentXP = 0, level = 1, className = '' }) => {
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Thresholds mirror the backend config/levels.py — never recompute them locally
  const safeLevel = Math.min(Math.max(level, 1), MAX_LEVEL);
  const xpForCurrentLevel = LEVEL_XP_THRESHOLDS[safeLevel] ?? 0;
  const xpForNextLevel = LEVEL_XP_THRESHOLDS[safeLevel + 1] ?? null;
  const isMaxLevel = !xpForNextLevel;

  const xpInCurrentLevel = Math.max(0, currentXP - xpForCurrentLevel);
  const xpNeededForNext = isMaxLevel ? xpInCurrentLevel : xpForNextLevel - xpForCurrentLevel;
  const progressPercent = isMaxLevel
    ? 100
    : Math.min(100, Math.max(0, (xpInCurrentLevel / xpNeededForNext) * 100));

  useEffect(() => {
    setIsAnimating(true);
    const timeout = setTimeout(() => {
      setProgress(progressPercent);
      setTimeout(() => setIsAnimating(false), 600);
    }, 100);
    return () => clearTimeout(timeout);
  }, [progressPercent]);

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Niveau {safeLevel}</span>
        <span data-testid="level-progress-label">
          {isMaxLevel
            ? 'Niveau maximum atteint'
            : `${xpInCurrentLevel.toLocaleString('fr-FR')} / ${xpNeededForNext.toLocaleString('fr-FR')} XP`}
        </span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden relative">
        <motion.div
          className="h-full rounded-full relative"
          style={{
            background: 'linear-gradient(90deg, #F59E0B, #FBBF24, #F59E0B)',
            backgroundSize: '200% 100%',
          }}
          initial={{ width: 0 }}
          animate={{ 
            width: `${progress}%`,
            backgroundPosition: isAnimating ? ['0% 0%', '100% 0%'] : '0% 0%'
          }}
          transition={{ 
            width: { duration: 0.8, ease: "easeOut" },
            backgroundPosition: { duration: 1, repeat: isAnimating ? 2 : 0 }
          }}
        >
          {/* Shine effect */}
          {isAnimating && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AnimatedLevelProgress;
